/**
 * Inbound-campaign row shape. Most fields overlap with the outbound voice
 * campaign row (status code, template id, counters) so we reuse the voice
 * STATUS_MAP / TEMPLATES helpers from `features/campaigns/types`.
 *
 * Inbound-only fields: `did` (the Direct Inward Dial number the caller
 * rings) and `cliList` (used as the friendly CLI label in admin views).
 */
export interface InboundCampaignRow {
  campaignId: number;
  campaignName: string;
  campaignStatus: number;
  templateId: number;

  /** DID (the inbound phone number that triggers this campaign). */
  did?: string;
  cliList?: string;

  scheduleTime?: string;
  startTime?: string;
  endTime?: string;

  numbersProcessed?: number;
  callsConnected?: number;
  pulses?: number;
  dtmfCount?: number;
  smsCount?: number;
  ttsCount?: number;
  retries?: number;
  retryCount?: number;

  /* Admin/owner fields — only on the super-admin endpoint. */
  userId?: number;
  username?: string;
  name?: string;
}

export interface InboundCampaignListRequest {
  userId: number;
  /** Free-text campaign name filter (server-side). */
  campaignName: string;
  /** Free-text DID filter (server-side). */
  did: string;
}

export interface InboundCampaignHistoricalRequest
  extends InboundCampaignListRequest {
  startDate: string;
  endDate: string;
}

/* ---------------- Compose ---------------- */

export interface InboundGroupRow {
  groupId: string;
  groupName: string;
  agentDtmf: string;
}

/**
 * Wire-format body for POST /api/obd/inbound/campaign/compose.
 * `groupRows` is the legacy JSON-string-of-`{groupRows: [...]}` wrap, same
 * pattern as voice patch lists.
 */
export interface InboundCampaignComposeRequest {
  userId: number;
  campaignName: string;
  /** Always "6" for inbound — preserved as a string per legacy. */
  templateId: string;
  dtmf: string;
  welcomePId: string;
  menuPId: string;
  noInputPId: string;
  wrongInputPId: string;
  thanksPId: string;
  noAgentId: string;
  smsSuccessApi: string;
  smsSuccessMessage: string;
  smsFailApi: string;
  smsFailMessage: string;
  smsDtmfApi: string;
  smsDtmfMessage: string;
  callDurationSMS: number;
  retries: number;
  retryInterval: number;
  /** JSON-stringified `{ groupRows: [{groupId, groupName, agentDtmf}] }`. */
  groupRows: string;
  menuWaitTime: string;
  rePrompt: string;
  ivrId: string;
  /** JSON-stringified `{ cliList: [...] }`; empty string when none picked. */
  clis: string;
  /** Legacy hardcoded the location to "indore" — preserved. */
  location: string;
}

export interface RunningDidRow {
  did?: string;
  cliId?: number;
  cli?: string;
}

/* ---------------- Compose form values (UI state) ---------------- */

export interface InboundComposeFormValues {
  campaignName: string;
  /** Voice files (all optional except no-agent which is required for patch). */
  useWelcomeFile: boolean;
  useNoinputFile: boolean;
  useWronginputFile: boolean;
  useThanksFile: boolean;
  welcomeId: string;
  menuId: string;
  noinputId: string;
  wronginputId: string;
  thanksId: string;
  noAgentId: string;
  /** DTMF digits the menu accepts (drives the agent-group → DTMF picker). */
  dtmf: string[];
  menuWaitTime: string;
  rePrompt: string;
  /** Group rows: each row binds an agent group to one DTMF digit. */
  groupRows: InboundGroupRow[];
  /** Retry settings. */
  retries: number;
  retryInterval: number;
  /** CLIs (DIDs) to use — pulled from dedicated CLI pool. */
  selectedCLI: string[];
  /** SMS hooks. */
  sendSmsSuccess: boolean;
  smsApiSuccess: string;
  callDurationSMS: number;
  sendSmsFail: boolean;
  smsApiFail: string;
  sendSmsDtmf: boolean;
  smsApiDtmf: string;
}

export const DEFAULT_INBOUND_FORM_VALUES: InboundComposeFormValues = {
  campaignName: '',
  useWelcomeFile: false,
  useNoinputFile: false,
  useWronginputFile: false,
  useThanksFile: false,
  welcomeId: '',
  menuId: '',
  noinputId: '',
  wronginputId: '',
  thanksId: '',
  noAgentId: '',
  dtmf: [],
  menuWaitTime: '',
  rePrompt: '',
  groupRows: [],
  retries: 0,
  retryInterval: 0,
  selectedCLI: [],
  sendSmsSuccess: false,
  smsApiSuccess: '',
  callDurationSMS: 0,
  sendSmsFail: false,
  smsApiFail: '',
  sendSmsDtmf: false,
  smsApiDtmf: '',
};
